/**
 * Marketing Context Composable
 *
 * Provides unified context for all marketing components and landing pages.
 * Combines landing page variant, personalization data, and user context
 * into a single object that can be passed to tracking events.
 *
 * IMPORTANT: This composable MUST be used only inside setup() or components.
 * For use outside setup, use the component-injected context instead.
 *
 * This ensures consistent tracking across all marketing touchpoints.
 */

import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { usePersonalization } from './usePersonalization'
import type { ConcernType } from './usePersonalization'

export interface MarketingContext {
  // Landing page context
  landingPageVariant: string
  landingPageId: string | null

  // Personalization context
  concerns: ConcernType[]
  dominantConcern: ConcernType
  dominantLearningStyle: string

  // User context
  isPersonalized: boolean
  childName: string
  childAge?: number
  childGender?: string

  // Tracking flags
  hasPersonalization: boolean
  isFirstVisit: boolean
}

/**
 * Get unified marketing context for tracking
 * MUST be called inside setup() or components only
 */
export function useMarketingContext() {
  const route = useRoute()
  const { personalizationProfile } = usePersonalization()

  // Determine if this is first visit (no personalization data yet)
  const isFirstVisit = computed(() => {
    return !personalizationProfile.value.childName ||
           personalizationProfile.value.childName === 'Ihr Kind'
  })

  const marketingContext = computed<MarketingContext>(() => {
    const variantParam = route.params.variant
    const variant = typeof variantParam === 'string' ? variantParam : 'default'
    const routeName = route.name ? String(route.name) : null

    return {
      // Landing page context
      landingPageVariant: variant,
      landingPageId: routeName,

      // Personalization context
      concerns: personalizationProfile.value.concerns,
      dominantConcern: personalizationProfile.value.dominantConcern,
      dominantLearningStyle: personalizationProfile.value.dominantLearningStyle,

      // User context
      isPersonalized: personalizationProfile.value.concerns.length > 0 &&
                     personalizationProfile.value.concerns[0] !== 'NONE',
      childName: personalizationProfile.value.childName,

      // Tracking flags
      hasPersonalization: personalizationProfile.value.concerns.length > 0,
      isFirstVisit: isFirstVisit.value,
    }
  })

  return { marketingContext }
}

/**
 * Get marketing context as plain object (for event data)
 * MUST be called inside setup() or components only
 *
 * Returns properly typed tracking metadata
 */
export function getMarketingContextData(): Record<string, string | number | boolean> {
  const { marketingContext } = useMarketingContext()
  const ctx = marketingContext.value

  return {
    landing_page_variant: ctx.landingPageVariant,
    landing_page_id: ctx.landingPageId || 'unknown',
    concerns: ctx.concerns.join(','),
    dominant_concern: ctx.dominantConcern,
    dominant_learning_style: ctx.dominantLearningStyle,
    is_personalized: ctx.isPersonalized,
    child_name: ctx.childName,
    has_personalization: ctx.hasPersonalization,
    is_first_visit: ctx.isFirstVisit,
  }
}

/**
 * Check if specific concern is present in context
 * MUST be called inside setup() or components only
 */
export function hasConcernInContext(concern: ConcernType): boolean {
  const { marketingContext } = useMarketingContext()
  return marketingContext.value.concerns.includes(concern)
}

/**
 * Get concern-specific tracking data
 * MUST be called inside setup() or components only
 *
 * Returns properly typed tracking metadata
 */
export function getConcernTrackingData(): Record<string, string | number> {
  const { marketingContext } = useMarketingContext()
  const ctx = marketingContext.value

  if (!ctx.isPersonalized) {
    return { concern_type: 'none' }
  }

  return {
    concern_type: ctx.dominantConcern,
    all_concerns: ctx.concerns.join(','),
    concern_count: ctx.concerns.length,
  }
}
